export type StatusPrazo = 'sem_prazo' | 'no_prazo' | 'alerta' | 'atrasado' | 'concluido';

export interface StatusPrazoConfig {
  label: string;
  severity: 'secondary' | 'success' | 'warn' | 'danger' | 'info';
  icon: string;
}

const STATUS_PRAZO_MAP: Record<StatusPrazo, StatusPrazoConfig> = {
  sem_prazo: { label: 'Sem Prazo', severity: 'secondary', icon: 'pi pi-minus-circle' },
  no_prazo: { label: 'No Prazo', severity: 'success', icon: 'pi pi-check-circle' },
  alerta: { label: 'Alerta', severity: 'warn', icon: 'pi pi-exclamation-triangle' },
  atrasado: { label: 'Atrasado', severity: 'danger', icon: 'pi pi-times-circle' },
  concluido: { label: 'Concluído', severity: 'info', icon: 'pi pi-flag' },
};

export function getStatusPrazoConfig(status: string | null | undefined): StatusPrazoConfig {
  if (!status || !(status in STATUS_PRAZO_MAP)) {
    return STATUS_PRAZO_MAP.sem_prazo;
  }
  return STATUS_PRAZO_MAP[status as StatusPrazo];
}

/**
 * Retorna o rótulo legível para um status de prazo.
 */
export function getStatusPrazoLabel(status: string | null | undefined): string {
  return getStatusPrazoConfig(status).label;
}

/**
 * Retorna o severity para componentes PrimeVue.
 */
export function getStatusPrazoSeverity(status: string | null | undefined): string {
  return getStatusPrazoConfig(status).severity;
}

/**
 * Verifica se o prazo está expirado.
 */
export function isPrazoExpirado(status: string | null | undefined): boolean {
  return status === 'atrasado';
}

/**
 * Verifica se o trabalho foi concluído.
 */
export function isPrazoConcluido(status: string | null | undefined): boolean {
  return status === 'concluido';
}

/**
 * Normaliza uma string de data (ISO 8601 ou yyyy-mm-dd) para yyyy-mm-dd.
 */
function normalizeDateStr(date: string): string {
  return date.split('T')[0];
}

/**
 * Formata uma data (ISO 8601 ou yyyy-mm-dd) para o formato brasileiro (dd/mm/yyyy).
 */
export function formatDateBR(date: string | null | undefined): string {
  if (!date) return '-';
  const dateOnly = normalizeDateStr(date);
  const parts = dateOnly.split('-');
  if (parts.length !== 3) return '-';
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

/**
 * Calcula quantos dias restam até a data final.
 * Retorna negativo se já passou, null se não houver data.
 */
export function calcularDiasRestantes(dataFim: string | null | undefined): number | null {
  if (!dataFim) return null;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const dateOnly = normalizeDateStr(dataFim);
  const fim = new Date(dateOnly + 'T00:00:00');
  if (isNaN(fim.getTime())) return null;
  const diffMs = fim.getTime() - hoje.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Retorna mensagem descritiva sobre os dias restantes.
 */
export function getMensagemDiasRestantes(dataFim: string | null | undefined): string {
  const dias = calcularDiasRestantes(dataFim);
  if (dias === null) return 'Prazo não definido';
  if (dias < 0) return `Prazo expirado há ${Math.abs(dias)} dia(s)`;
  if (dias === 0) return 'Prazo expira hoje';
  if (dias === 1) return 'Falta 1 dia para o prazo';
  return `Faltam ${dias} dias para o prazo`;
}
